import React, { useEffect, useState } from "react";
import "./ApiDetails.css";
import { APIDetailsHeader } from "../common/ApiDetailsTabHeader";
import {
  CardBody,
  CardWrapper,
  CardHeader,
  MenuListItem,
  IconChevronLeft,
  IconChevronRight,
  IconCheckCircle,
  IconSales,
} from "cdk-radial";
import { DAABPricingPlanTile } from "../pricing-plans/DAABPricingPlanTile";
import { PRICING_PLAN_STATE } from "../../stories/DAABAPIDetailsData";
import { getInstanceIdVersionMap } from "../../utils/spec-utils";
import { getAllEndpoints } from "../../utils/api-pricing";
import { API_TYPE } from "./constants";
import { RESOURCE_TYPE } from "../../utils/constants";
import { BundlePriceDialog } from '../pricing-plans/BundlePriceDialog';

const BundlePricing = (props) => {

  const isPriceBundle =
    props?.bundleDetails?.plans?.length > 0;

  const [selectedApi, setSelectedApi] = useState(() => {
    return Array.isArray(props.bundleApis) && props.bundleApis.length > 0
      ? props.bundleApis[0]
      : null;
  });
  const apiSpec = selectedApi?.environments?.[0]?.instances?.[0]?.spec;
  const isAsync = selectedApi?.type === API_TYPE.ASYNC;

  const [isLoading, setIsLoading] = useState(
    props?.addingPricingPlanToApiInCart || props?.addingAPIToCart
  );

  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesPerPage = 4;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDialogPlan, setSelectedDialogPlan] = useState(null);

  const openBundlePriceDialog = (plan) => {
    setSelectedDialogPlan(plan);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedDialogPlan(null);
  };

  useEffect(() => {
    setIsLoading(
      props?.addingPricingPlanToApiInCart ||
        props?.addingAPIToCart ||
        props?.fetchingCartDetails
    );
  }, [
    props?.addingPricingPlanToApiInCart,
    props?.addingAPIToCart,
    props?.fetchingCartDetails,
  ]);

  useEffect(() => {
    if (Array.isArray(props?.bundleApis) && props.bundleApis.length > 0) {
      setSelectedApi((prevApi) => {
        const updatedApi = props?.bundleApis?.find(
          (api) => api?.id === prevApi?.id
        );
        return updatedApi || props?.bundleApis[0];
      });
    }
  }, [props?.bundleApis]);

  const handleApiSelection = (api) => {
    setSelectedApi(api);
    setCurrentSlide(0); // Reset to the first slide when a new API is selected
  };

  const handleNext = () => {
    setCurrentSlide((prev) => prev + 1);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => prev - 1);
  };

  const getPricingPlanState = (selectedApi, plan) => {
    if (selectedApi?.isContracted) {
      return PRICING_PLAN_STATE.contracted;
    }
    if (selectedApi?.inOrder) {
      return;
    }
    if (selectedApi?.isInCart) {
      return plan?.isPricingPlanSelected
        ? PRICING_PLAN_STATE.selectedAndInCart
        : PRICING_PLAN_STATE.inCart;
    }
    return PRICING_PLAN_STATE.notInCart;
  };

  const getBundlePricingPlanState = (bundleDetails, plan) => {
    if (bundleDetails?.isContracted) {
      return PRICING_PLAN_STATE.contracted;
    }

    if (bundleDetails?.inOrder) {
      return PRICING_PLAN_STATE.purchased;
    }

    if (props?.isCurrentBundleInCart) {
      const isThisPlanSelected =
        plan?.id === props?.selectedBundlePlanId || plan?.isSelected;
      return isThisPlanSelected
        ? PRICING_PLAN_STATE.selectedAndInCart
        : PRICING_PLAN_STATE.inCart;
    }

    return PRICING_PLAN_STATE.notInCart;
  };

  const visiblePlans = selectedApi?.plans?.slice(
    currentSlide * slidesPerPage,
    (currentSlide + 1) * slidesPerPage
  );

  const isLeftArrowDisabled = currentSlide === 0;
  const isRightArrowDisabled =
    currentSlide >= Math.ceil(selectedApi?.plans?.length / slidesPerPage) - 1;

  const handlePricingPlanSelect = (planId) => {
    props?.apiDetailsOnClickHandlers?.selectPricingPlan({
      resourceId: selectedApi.id,
      planId,
      resourceType: selectedApi?.type,
    });
  };

  const handleBundlePricingPlanSelect = (planId) => {
    props?.apiDetailsOnClickHandlers?.selectBundlePricingPlan({
      resourceId: props.bundleDetails.id,
      planId,
      resourceType: RESOURCE_TYPE.BUNDLE,
      bundleApis: props.bundleApis,
    });
  };

  const handleExpandContainerDetails = ({ apiId, apiType }) => {
    props?.apiHomeOnClickHandlers?.fetchApiDetails({
      apiId: apiId,
      apiType: apiType,
      bundleId: props.bundleDetails.id,
    }).catch(e => console.error(`Error fetching API details for ${apiId}:`, e));
  };

  return (
    <div>
      <APIDetailsHeader title="Pricing" />
      <div className="api-dir-pkg_api-pricing-selection-outer-container">
        <div className="api-dir-pkg_pricing-section-desc">
          {isPriceBundle
            ? `Review what’s included in the bundle and the pricing.`
            : `Review and select Pricing for each API in the bundle.`}
        </div>
        <div className="api-dir-pkg_api-pricing-plan-selection-container">
          {!isPriceBundle && (
            <div className="api-dir-pkg_api-pricing-plan-selection-left-section-wrapper">
              <CardWrapper className="api-dir-pkg_api-pricing-plan-selection-left-section">
                <CardBody className="api-dir-pkg_api-pricing-plan-selection-left-section-card-body">
                  <CardHeader
                    className="api-dir-pkg_bundle-pricing-selection-card-title"
                    title="API Name"
                  />
                  <div className="api-dir-pkg_api-name-item">
                    {props?.bundleApis?.map((f) => (
                      <MenuListItem
                        key={f.id}
                        onClick={() => handleApiSelection(f)}
                        dataTestId="menu-list-item"
                        isSelected={selectedApi.id === f?.id ? true : false}
                        hideSelectedItemCheckmark={true}
                        className={
                          selectedApi.id === f.id
                            ? "api-dir-pkg_selected-api-name-item"
                            : "api-dir-pkg_unselected-api-name-item"
                        }
                      >
                        <span className="api-dir-pkg_api-menu-list-wrapper">
                          <div className="api-dir-pkg_select-api-item-card-api-row">
                            {(f.hasPlanSelected && f.isInCart) ||
                            f.inOrder ||
                            f.isContracted ? (
                              <span className="api-dir-pkg_plan-selected-check-icon">
                                <IconCheckCircle className="api-dir-pkg_icon-check-cicle" />
                              </span>
                            ) : (
                              <span className="api-dir-pkg_plan-unselected"></span>
                            )}
                            <div
                              className={
                                selectedApi.id === f.id
                                  ? "api-dir-pkg_selected-api-item-api-name"
                                  : "api-dir-pkg_select-api-item-api-name"
                              }
                            >
                              {f.name}
                            </div>
                          </div>
                          {f?.isContracted && <IconSales />}
                        </span>
                      </MenuListItem>
                    ))}
                  </div>
                </CardBody>
              </CardWrapper>
            </div>
          )}
          <div
            className={`api-dir-pkg_bundle-pricing-plan-selection-right-section ${
              isPriceBundle
                ? "api-dir-pkg_bundle-pricing-plan-selection-right-section--full-width"
                : ""
            }`}
          >
            <div className="api-dir-pkg_pricing-plans-cards-container">
              {!isPriceBundle && selectedApi?.plans?.length > 0 ? (
                <>
                  <div
                    className={`api-dir-pkg_scroll-left-button ${
                      isLeftArrowDisabled ? "hidden" : ""
                    }`}
                    onClick={handlePrev}
                    data-testid="scroll-left-button"
                  >
                    <IconChevronLeft />
                  </div>
                  <div className="api-dir-pkg_pricing-plans-cards">
                    {visiblePlans?.map((plan, index) => (
                      <DAABPricingPlanTile
                        key={`${plan?.id}-${index}`}
                        api={selectedApi}
                        isApiInCart={selectedApi?.isInCart}
                        plan={plan}
                        {...getPricingPlanState(selectedApi, plan)}
                        selectPricingPlan={handlePricingPlanSelect}
                        isLoading={isLoading}
                        apiId={selectedApi?.id}
                        apiType={selectedApi?.type}
                        pricingPlanId={plan?.id}
                        isAPIPurchased={selectedApi?.inOrder}
                        isSelected={plan?.isPricingPlanSelected}
                        isMsaConsented={props?.isMsaConsented}
                        expandContainerDetails={handleExpandContainerDetails}
                        instanceIdVersionMap={getInstanceIdVersionMap(
                          selectedApi
                        )}
                        isAsync={isAsync}
                        allEndpoints={getAllEndpoints(apiSpec, isAsync)}
                        isBundlePricing={true}
                        fetchingAPIDetailsInBundle={
                          props.fetchingAPIDetailsInBundle
                        }
                        isCurrentApiInBundleCart={
                          props.isCurrentApiInBundleCart
                        }
                        isBundle={false}
                      />
                    ))}
                  </div>
                  <div
                    className={`api-dir-pkg_scroll-right-button ${
                      isRightArrowDisabled ? "hidden" : ""
                    }`}
                    onClick={handleNext}
                    data-testid="scroll-right-button"
                  >
                    <IconChevronRight />
                  </div>
                </>
              ) : isPriceBundle && props?.bundleDetails?.plans?.length > 0 ? (
                <div className="api-dir-pkg_pricing-plans-cards">
                  {props.bundleDetails.plans.map((bundlePlan, index) => {
                    const bundlePlanState = getBundlePricingPlanState(
                      props.bundleDetails,
                      bundlePlan
                    );

                    const bundleInCart = props.cartDetails?.find(
                      (cartItem) =>
                        cartItem.resourceType === RESOURCE_TYPE.BUNDLE &&
                        cartItem.resourceId === props.bundleDetails?.id
                    );

                    const isThisPlanSelected =
                      bundleInCart?.resourcePricingPlanId === bundlePlan?.id ||
                      props.orderDetails?.bundleIntegrations?.find(
                        (bundle) => bundle.id === props.bundleDetails?.id
                      )?.integrations?.find(
                        (integration) => integration.pricingPlanId === bundlePlan?.id
                      ) !== undefined;

                    const isBundlePurchased = props.orderDetails?.bundleIntegrations?.some(
                      (bundle) => bundle.id === props.bundleDetails?.id
                    );

                    return (
                      <DAABPricingPlanTile
                        key={`bundle-${bundlePlan?.id}-${index}`}
                        api={props.bundleDetails}
                        isApiInCart={props.isCurrentBundleInCart}
                        plan={bundlePlan}
                        {...bundlePlanState}
                        selectPricingPlan={handleBundlePricingPlanSelect}
                        openBundlePriceDialog={openBundlePriceDialog}
                        isLoading={isLoading}
                        apiId={props.bundleDetails?.id}
                        apiType={RESOURCE_TYPE.BUNDLE}
                        pricingPlanId={bundlePlan?.id}
                        isAPIPurchased={isBundlePurchased || false}
                        isSelected={isThisPlanSelected || false}
                        isMsaConsented={props?.isMsaConsented}
                        isBundlePricing={true}
                        isBundle={true}
                        isAPIContracted={
                          props.bundleDetails?.isContracted || false
                        }
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <BundlePriceDialog
        isOpen={dialogOpen}
        onClose={closeDialog}
        plan={selectedDialogPlan}
        isAsync={isAsync}
      />
    </div>
  );
};

export default BundlePricing;
